import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { releaseNotesApi } from '../../../../api/release-notes';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { useDebouncedValue } from '../../../../lib/useDebouncedValue';
import { ReleaseNoteSendBody, ReleaseNoteSendResult } from '../../../../types';
import { ConfirmSendDialog, RecipientList } from '../components';
import {
  MAX_RECIPIENTS, canPick, errorMessage, filterRecipients, pickableOnly, sendBlocker, summarizeResult, toSendBody, toggleId,
} from '../utils';

export function SendTab() {
  const queryClient = useQueryClient();
  const [chosenVersion, setChosenVersion] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [resend, setResend] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<ReleaseNoteSendResult | null>(null);
  const debouncedSearch = useDebouncedValue(search, 250);

  const notes = useQuery({
    queryKey: ['release-notes'],
    queryFn: ({ signal }) => releaseNotesApi.list(signal).then((r) => r.data),
  });
  // The newest note is the default, until one is picked.
  const version = chosenVersion || notes.data?.[0]?.version || '';

  const preview = useQuery({
    queryKey: ['release-note', version],
    queryFn: ({ signal }) => releaseNotesApi.get(version, signal).then((r) => r.data),
    enabled: Boolean(version),
  });
  const recipients = useQuery({
    queryKey: ['release-note-recipients', version],
    queryFn: ({ signal }) => releaseNotesApi.recipients(version, signal).then((r) => r.data),
    enabled: Boolean(version),
  });

  const all = useMemo(() => recipients.data ?? [], [recipients.data]);
  const visible = useMemo(() => filterRecipients(all, debouncedSearch), [all, debouncedSearch]);
  const alreadySentCount = useMemo(
    () => all.filter((r) => selected.includes(r.id) && r.sentAt !== null).length,
    [all, selected],
  );
  const blocker = sendBlocker(selected.length);

  const send = useMutation({
    mutationFn: (body: ReleaseNoteSendBody) => releaseNotesApi.send(version, body).then((r) => r.data),
    onSuccess: (res) => {
      setResult(res);
      setConfirmOpen(false);
      // Whoever failed stays picked, so one click retries them.
      setSelected(res.failed.map((f) => f.id));
      queryClient.invalidateQueries({ queryKey: ['release-notes'] });
      queryClient.invalidateQueries({ queryKey: ['release-note-recipients', version] });
      queryClient.invalidateQueries({ queryKey: ['release-note-sends'] });
    },
  });

  const changeVersion = useCallback((next: string) => {
    setChosenVersion(next);
    setSelected([]);
    setResult(null);
  }, []);

  const changeResend = useCallback((checked: boolean) => {
    setResend(checked);
    setSelected((current) => pickableOnly(current, all, checked));
  }, [all]);

  const toggle = useCallback((id: string) => setSelected((current) => toggleId(current, id)), []);

  const selectVisible = useCallback(() => {
    setSelected((current) => Array.from(new Set(current.concat(visible.filter((r) => canPick(r, resend)).map((r) => r.id)))));
  }, [visible, resend]);

  const confirm = useCallback(() => send.mutate(toSendBody(selected, all, resend)), [send, selected, all, resend]);

  const nameOf = useCallback((id: string) => all.find((r) => r.id === id)?.name ?? id, [all]);

  if (notes.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (notes.isError) {
    return <p className="text-sm text-destructive">{errorMessage(notes.error, 'Could not load the release notes.')}</p>;
  }
  if (!notes.data || notes.data.length === 0) {
    return <p className="text-sm text-muted-foreground">No release notes have been shipped yet.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground" htmlFor="release-note-version">Release note</label>
        <Select value={version} onValueChange={changeVersion}>
          <SelectTrigger id="release-note-version" className="w-72">
            <SelectValue placeholder="Pick a version" />
          </SelectTrigger>
          <SelectContent>
            {notes.data.map((note) => (
              <SelectItem key={note.version} value={note.version}>
                {`v${note.version} (sent to ${note.sentCount})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Preview</h2>
          {preview.isError ? (
            <p className="text-sm text-destructive">{errorMessage(preview.error, 'Could not load the preview.')}</p>
          ) : (
            // sandbox="" blocks scripts and navigation: it is only a picture of the email.
            <iframe
              title="Release note preview"
              sandbox=""
              srcDoc={preview.data?.html ?? ''}
              className="h-[560px] w-full rounded-md border bg-white"
            />
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold">Recipients</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search recipients"
              className="w-56"
            />
            <Button variant="outline" size="sm" onClick={selectVisible}>Select all shown</Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected([])} disabled={selected.length === 0}>Clear</Button>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={resend}
              onChange={(e) => changeResend(e.target.checked)}
            />
            Send again to people who already got it
          </label>

          {recipients.isError ? (
            <p className="text-sm text-destructive">{errorMessage(recipients.error, 'Could not load the users.')}</p>
          ) : (
            <RecipientList recipients={visible} selected={selected} resend={resend} onToggle={toggle} />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => { send.reset(); setConfirmOpen(true); }} disabled={blocker !== null}>
              {`Send to ${selected.length}`}
            </Button>
            <span className="text-sm text-muted-foreground">
              {blocker === 'too-many'
                ? `Pick at most ${MAX_RECIPIENTS} people at a time.`
                : `${selected.length} selected`}
            </span>
          </div>

          {result && (
            <div role="status" className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
              <p>{summarizeResult(result)}</p>
              {result.failed.length > 0 && (
                <ul className="list-disc pl-5 text-destructive">
                  {result.failed.map((f) => (
                    <li key={f.id}>{`${nameOf(f.id)} (${f.email}): ${f.error}`}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>

      <ConfirmSendDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        version={version}
        count={selected.length}
        alreadySentCount={alreadySentCount}
        pending={send.isPending}
        error={send.isError ? errorMessage(send.error, 'Could not send the release note.') : null}
        onConfirm={confirm}
      />
    </div>
  );
}
